/**
 * Evidence Extractor Page Component
 *
 * Main page component for the Evidence Extractor application in the unified routing system.
 * Handles routing and provides the main interface for evidence extraction functionality.
 * Integrates with shared auth components for authentication.
 *
 * Routes:
 * - /                    - Document management (landing page)
 * - /documents/:id       - Document details with versions
 * - /review/:extractionId - Multi-page review interface
 * - /review/:extractionId/:page - Specific review page (1-4)
 */

import React, { useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { AuthGuard } from '../../shared/src/auth';
import { DocumentsPage } from './pages/DocumentsPage';
import { ReviewPage } from './pages/ReviewPage';
import { ComparePage } from './pages/ComparePage';
import { EvidenceExtractor } from './pages/EvidenceExtractor';
import { DemoModeIndicator } from './components/DemoModeIndicator';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { DocumentProvider } from './contexts/DocumentContext';
import { ExtractionProvider } from './contexts/ExtractionContext';

/**
 * Inner component that uses navigation hooks
 * Separated to allow ErrorBoundary to wrap it properly
 */
const EvidenceExtractorContent: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigateHome = useCallback(() => {
    navigate('/evidence-extractor');
  }, [navigate]);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <ErrorBoundary
      onNavigateHome={handleNavigateHome}
      onRetry={handleRetry}
      errorTitle="Something went wrong"
      errorMessage="We encountered an unexpected error in the Evidence Extractor. Please try again or return to the documents page."
    >
      <DocumentProvider>
        <ExtractionProvider>
          <Layout>
            <DemoModeIndicator />
            <Routes>
              {/* Documents - landing page */}
              <Route index element={<DocumentsPage />} />

              {/* Document details */}
              <Route path="documents/:id" element={<DocumentsPage />} />

              {/* Review interface routes */}
              <Route path="review/:extractionId" element={<ReviewPage />} />
              <Route path="review/:extractionId/:page" element={<ReviewPage />} />

              {/* Comparison route */}
              <Route path="compare/:leftId/:rightId" element={<ComparePage />} />

              {/* Legacy route - original Evidence Extractor page */}
              <Route path="legacy" element={<EvidenceExtractor />} />

              {/* Catch-all - redirect to documents */}
              <Route path="*" element={<DocumentsPage />} />
            </Routes>
          </Layout>
        </ExtractionProvider>
      </DocumentProvider>
    </ErrorBoundary>
  );
};

/**
 * Main Evidence Extractor page component with routing support
 *
 * Features:
 * - Documents-first landing page for evidence extraction
 * - Requires authentication for all routes
 * - Provides document and extraction context providers
 */
export const EvidenceExtractorPage: React.FC = () => {
  return (
    <AuthGuard>
      <EvidenceExtractorContent />
    </AuthGuard>
  );
};
